import { useCallback, useEffect, useRef, useState } from 'react';
import ReferenceScreen from '../../components/reference/ReferenceScreen';
import RowStatus from '../../components/reference/RowStatus';
import FormAlert from '../../components/reference/FormAlert';
import { referenceApi } from '../../services/referenceApi';
import { apiErrorMessage, blankToNull, isNotFound } from '../../services/apiErrors';
import type { FuturesContract, HedgeMapping, Ingredient } from '../../services/referenceApi';

type Editing = { kind: 'new' } | { kind: 'edit'; row: HedgeMapping } | null;

function byIngredient(a: HedgeMapping, b: HedgeMapping) {
  return a.ingredient_name.localeCompare(b.ingredient_name);
}

const EMPTY = { ingredientId: '', contractId: '', ratio: '' };

/** Which futures contract each ingredient hedges on, and at what ratio. */
export default function HedgingPage({ slug }: { slug: string }) {
  const [rows, setRows] = useState<HedgeMapping[] | null>(null);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [contracts, setContracts] = useState<FuturesContract[]>([]);
  const [loadError, setLoadError] = useState('');
  const [buyerMissing, setBuyerMissing] = useState(false);
  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState(EMPTY);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const [switchError, setSwitchError] = useState('');
  const firstFieldRef = useRef<HTMLSelectElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setLoadError('');
    setBuyerMissing(false);
    setEditing(null);
    setSwitchError('');
    setSwitching(null);
    Promise.all([referenceApi.listMappings(slug), referenceApi.listIngredients(slug), referenceApi.listContracts(slug)])
      .then(([mappings, ingredientList, contractList]) => {
        if (cancelled) return;
        setRows([...mappings.data].sort(byIngredient));
        setIngredients(ingredientList.data);
        setContracts(contractList.data);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isNotFound(err)) setBuyerMissing(true);
        else setLoadError('We could not load the hedge mappings just now. Check your connection and try again.');
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (editing) firstFieldRef.current?.focus();
  }, [editing]);

  const contractName = (id: string) => contracts.find((c) => c.id === id)?.name ?? '';

  const open = useCallback((next: Editing) => {
    setEditing(next);
    setSaveError('');
    setForm(
      next?.kind === 'edit'
        ? { ingredientId: next.row.ingredient_id, contractId: next.row.contract_id, ratio: next.row.ratio ? String(Number(next.row.ratio)) : '' }
        : EMPTY
    );
  }, []);

  const set = (field: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const place = (saved: HedgeMapping) =>
    setRows((current) => [...(current ?? []).filter((r) => r.id !== saved.id), saved].sort(byIngredient));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setSaveError('');
    setSaving(true);
    // A blank ratio is sent as null on purpose: it means not set yet.
    const body = { contract_id: form.contractId, ratio: blankToNull(form.ratio) };
    try {
      const { data } =
        editing.kind === 'new'
          ? await referenceApi.addMapping(slug, { ...body, ingredient_id: form.ingredientId })
          : await referenceApi.editMapping(slug, editing.row.id, body);
      place(data);
      setEditing(null);
      addButtonRef.current?.focus();
    } catch (err) {
      setSaveError(apiErrorMessage(err, 'We could not save the hedge mapping just now. Check your connection and try again.'));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (row: HedgeMapping) => {
    setSwitchError('');
    setSwitching(row.id);
    try {
      const { data } = await referenceApi.editMapping(slug, row.id, { active: !row.active });
      place(data);
    } catch (err) {
      setSwitchError(
        apiErrorMessage(err, `We could not switch the mapping for ${row.ingredient_name} ${row.active ? 'off' : 'on'} just now.`)
      );
    } finally {
      setSwitching(null);
    }
  };

  // A new mapping is for an ingredient that has none yet; an ingredient has one mapping at most.
  const mapped = new Set((rows ?? []).map((r) => r.ingredient_id));
  const ingredientOptions = ingredients.filter((i) => i.active && !mapped.has(i.id));
  const contractOptions = contracts.filter((c) => c.active || c.id === form.contractId);
  const complete = (editing?.kind === 'edit' || form.ingredientId) && form.contractId;

  return (
    <ReferenceScreen
      slug={slug}
      title="Hedging"
      lead={(buyer) =>
        `The futures contract each of ${buyer}'s ingredients hedges on. An ingredient without its own contract hedges off a related one at a ratio.`
      }
      addLabel="Add mapping"
      onAdd={() => open({ kind: 'new' })}
      addButtonRef={addButtonRef}
      buyerMissing={buyerMissing}
    >
      {editing && (
        <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-md border border-line bg-surface p-6" noValidate>
          <h2 className="text-heading text-ink">
            {editing.kind === 'new' ? 'New hedge mapping' : `Edit hedging for ${editing.row.ingredient_name}`}
          </h2>
          <FormAlert lead="Mapping not saved." message={saveError} />
          <div className="grid gap-5 sm:grid-cols-2">
            {editing.kind === 'new' && (
              <div>
                <label htmlFor="hm-ingredient" className="label">
                  Ingredient
                </label>
                <select
                  id="hm-ingredient"
                  ref={firstFieldRef}
                  required
                  value={form.ingredientId}
                  onChange={set('ingredientId')}
                  className="input"
                >
                  <option value="">Choose an ingredient</option>
                  {ingredientOptions.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="hm-contract" className="label">
                Contract
              </label>
              <select
                id="hm-contract"
                ref={editing.kind === 'edit' ? firstFieldRef : undefined}
                required
                value={form.contractId}
                onChange={set('contractId')}
                className="input"
              >
                <option value="">Choose a contract</option>
                {contractOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="hm-ratio" className="label">
                Ratio
              </label>
              <input
                id="hm-ratio"
                type="text"
                inputMode="decimal"
                aria-describedby="hm-ratio-hint"
                value={form.ratio}
                onChange={set('ratio')}
                className="input tabular-nums"
              />
              <p id="hm-ratio-hint" className="mt-1 text-caption text-ink-muted">
                Pounds of the contract&apos;s commodity hedged for each pound of the ingredient: 1 when it hedges on its own
                contract. Leave it blank until the ratio is agreed.
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={saving || !complete}>
              {saving ? 'Saving' : editing.kind === 'new' ? 'Save mapping' : 'Save changes'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8">
        {switchError && <FormAlert lead="Not switched." message={switchError} />}
        {loadError && <FormAlert lead="Hedge mappings not loaded." message={loadError} />}
        {rows === null && !loadError && (
          <p className="text-body text-ink-muted" role="status">
            Loading hedge mappings
          </p>
        )}
        {rows !== null && rows.length === 0 && <p className="text-body text-ink-muted">No hedge mappings yet.</p>}
        {rows !== null && rows.length > 0 && (
          // The table keeps its columns and scrolls inside its own box on a narrow screen. The box is
          // relative so the hidden Actions label stays inside it and cannot widen the page.
          <div className="relative overflow-x-auto">
            <table className="w-full border-collapse text-body">
              <caption className="sr-only">Hedge mappings</caption>
              <thead>
                <tr className="border-b border-line-strong text-left text-caption font-semibold text-ink-muted">
                  <th scope="col" className="py-2 pr-4">
                    Ingredient
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    Contract
                  </th>
                  <th scope="col" className="py-2 pr-4 text-right">
                    Ratio
                  </th>
                  <th scope="col" className="py-2 pr-4">
                    Status
                  </th>
                  <th scope="col" className="py-2 text-right">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className={`border-b border-line ${row.active ? 'text-ink' : 'text-ink-muted'}`}>
                    <td className="py-3 pr-4 font-semibold">{row.ingredient_name}</td>
                    <td className="py-3 pr-4">
                      <span className="tabular-nums">{row.contract_code}</span> {contractName(row.contract_id)}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">
                      {row.ratio ? String(Number(row.ratio)) : <span className="text-caption text-ink-muted">Ratio not set</span>}
                    </td>
                    <td className="py-3 pr-4">
                      <RowStatus active={row.active} />
                    </td>
                    <td className="py-3 text-right">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          className="text-caption font-semibold text-navy underline-offset-4 hover:underline"
                          onClick={() => open({ kind: 'edit', row })}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-caption font-semibold text-navy underline-offset-4 hover:underline disabled:opacity-60"
                          disabled={switching === row.id}
                          onClick={() => toggle(row)}
                        >
                          {row.active ? 'Switch off' : 'Switch on'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ReferenceScreen>
  );
}
